import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

// @material-ui
import { Grid, Typography } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { withStyles } from '@material-ui/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { State } from '_core/components/State'
import { BannerListItem } from '_core/components/BannerListItem'

// style
import { bannersListStyle } from './bannersListStyle'

const RawBannersList = props => {
  const {
    classes,
    renderItem,
    renderEmpty,
    renderError,
    renderFailure,
    emptyMessage,
    failureMessage,
    ItemComponent,
    initialPage,
    initialRows,
    inRowLg,
    inRowMd,
    inRowSm,
    inRowXs,
    baseUrl,
    width,
    path,
    onUpdate,
    hasPaging,
    hasPagingTop,
    hasPagingBottom,
    stateProps,
    itemProps,
    langInfo,
    getLangString: l,
  } = props

  const {
    request: { queue },
    config: {
      API_METHOD,
      DEFAULT_MESSAGE,
      API_ERROR: { DATA_WRONG, DATA_NOT_FOUND },
    },
  } = useGlobal()

  const countInRow = isWidthUp('lg', width)
    ? inRowLg
    : isWidthUp('md', width)
    ? inRowMd
    : isWidthUp('sm', width)
    ? inRowSm
    : inRowXs

  const [nodeKey, setNodeKey] = useState(0)
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialRows * countInRow)

  useEffect(() => {
    setLimit(initialRows * countInRow)
  }, [countInRow])

  useEffect(() => {
    setNodeKey(nk => nk + 1)
  }, [langInfo, path, page, limit])

  const getData = async () => {
    const data = await queue(API_METHOD.ARTICLE, {
      path,
      limit,
      page,
    })

    typeof onUpdate === 'function' && onUpdate(data)
    return data
  }

  const renderList = items =>
    items.map(item => {
      const rawItemProps = {
        baseUrl,
        data: item,
        ...itemProps,
      }

      const xs = items.length >= inRowXs ? 12 / inRowXs : 12
      const sm = items.length >= inRowSm ? 12 / inRowSm : 12
      const md = items.length >= inRowMd ? 12 / inRowMd : 12
      const lg = items.length >= inRowLg ? 12 / inRowLg : 12

      return (
        <Grid item={true} key={item.id} xs={xs} sm={sm} md={md} lg={lg}>
          {renderItem != null ? (
            renderItem(rawItemProps)
          ) : (
            <ItemComponent {...rawItemProps} />
          )}
        </Grid>
      )
    })

  const renderPaging = data =>
    hasPaging && data.hits > limit ? (
      <Pagination
        className={classes.paging}
        classes={{ ul: classes.pagingUl }}
        count={Math.ceil(data.hits / limit)}
        defaultPage={initialPage}
        page={page}
        onChange={(_, newPage) => setPage(newPage)}
        color="primary"
        size={isWidthUp('sm', width) ? 'large' : 'small'}
      />
    ) : null

  const renderEmptyMessage = () =>
    renderEmpty != null ? (
      renderEmpty()
    ) : (
      <Typography align="center" variant="h6" color="textSecondary">
        {emptyMessage != null ? l(emptyMessage) : l('r.nichego.ne.najdeno')}
      </Typography>
    )

  const renderErrorMessage = error =>
    renderError != null ? (
      renderError(error)
    ) : (
      <Typography align="center" variant="h6" color="textSecondary">
        {failureMessage != null ? l(failureMessage) : l(DEFAULT_MESSAGE.ERROR)}
      </Typography>
    )

  return (
    <State
      {...stateProps}
      key={nodeKey}
      getProps={{ data: getData }}
      render={({ data }) => {
        if (data.list.length > 0) {
          return (
            <>
              {hasPagingTop ? renderPaging(data) : null}
              <Grid container={true}>{renderList(data.list)}</Grid>
              {hasPagingBottom ? renderPaging(data) : null}
            </>
          )
        }

        return renderEmptyMessage()
      }}
      renderFailure={
        renderFailure != null
          ? renderFailure
          : ({ error }) =>
              [DATA_WRONG, DATA_NOT_FOUND].includes(error.code)
                ? renderEmptyMessage()
                : renderErrorMessage(error)
      }
    />
  )
}

RawBannersList.defaultProps = {
  initialPage: 1,
  initialRows: 3,
  hasPaging: true,
  hasPagingTop: true,
  hasPagingBottom: true,
  inRowLg: 3,
  inRowMd: 3,
  inRowSm: 3,
  inRowXs: 3,
  ItemComponent: BannerListItem,
}

RawBannersList.propTypes = {
  // self props
  ItemComponent: PropTypes.elementType,
  onUpdate: PropTypes.func,
  renderItem: PropTypes.func,
  renderEmpty: PropTypes.func,
  renderError: PropTypes.func,
  renderFailure: PropTypes.func,
  emptyMessage: PropTypes.string,
  failureMessage: PropTypes.string,
  hasPaging: PropTypes.bool,
  hasPagingTop: PropTypes.bool,
  hasPagingBottom: PropTypes.bool,
  inRowLg: PropTypes.number,
  inRowMd: PropTypes.number,
  inRowSm: PropTypes.number,
  inRowXs: PropTypes.number,
  initialPage: PropTypes.number,
  initialRows: PropTypes.number,
  stateProps: PropTypes.object,
  itemProps: PropTypes.object,
  baseUrl: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,

  // `withWidth` HOC props
  width: PropTypes.string.isRequired,

  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const BannersList = withLang(
  withWidth()(withStyles(bannersListStyle)(RawBannersList))
)
